// Deellinks (?share=...). Nieuw formaat: "z." + base64url(deflate-raw(JSON)).
// Fallback zonder CompressionStream: "u." + base64url(UTF-8 JSON).
// Oud formaat (legacy): btoa(encodeURIComponent(JSON)) — moet blijven werken.
import { toSharePayload } from './activities';
import type { Activity } from './types';

export const MAX_SHARE_DECODED_BYTES = 200 * 1024;
/** Maximale lengte van de ruwe ?share=-waarde. */
export const MAX_SHARE_PARAM_LENGTH = 400 * 1024;

const MSG_EMPTY = '⚠️ Deze deellink is leeg.';
const MSG_TOO_LONG = '⚠️ Deze deellink is te groot om veilig te importeren.';
const MSG_TOO_MUCH = '⚠️ Deze deellink bevat te veel gegevens (max. 200 KB).';
const MSG_BROKEN = '⚠️ Deze deellink is beschadigd.';
const MSG_NO_DECOMPRESS = '⚠️ Deze browser kan gecomprimeerde deellinks niet openen. Werk je browser bij.';
const MSG_NO_LIST = '⚠️ Deze deellink bevat geen lijst met activiteiten.';

/** Fout met een melding die direct aan de gebruiker getoond kan worden. */
export class ShareError extends Error {
  readonly userMessage: string;
  constructor(message: string) {
    super(message);
    this.name = 'ShareError';
    this.userMessage = message;
  }
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(str: string): Uint8Array {
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/').replace(/\s/g, '');
  if (!/^[A-Za-z0-9+/]*$/.test(b64)) throw new ShareError(MSG_BROKEN);
  while (b64.length % 4) b64 += '=';
  let binary: string;
  try {
    binary = atob(b64);
  } catch {
    throw new ShareError(MSG_BROKEN);
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Leest een stream uit met een harde limiet (bescherming tegen 'zip bombs'). */
async function readStreamWithLimit(stream: ReadableStream<Uint8Array>, maxBytes: number): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > maxBytes) {
      try {
        await reader.cancel();
      } catch {
        /* negeren */
      }
      throw new ShareError(MSG_TOO_MUCH);
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out;
}

function streamOf(bytes: Uint8Array): ReadableStream<Uint8Array> {
  return new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
}

function decodeUtf8(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new ShareError(MSG_BROKEN);
  }
}

/** Codeert activiteiten (compacte vorm zonder scores/id's) voor een deellink. */
export async function encodeSharePayload(activities: Activity[]): Promise<string> {
  const utf8 = new TextEncoder().encode(JSON.stringify(toSharePayload(activities)));
  if (typeof CompressionStream === 'function') {
    try {
      const stream = streamOf(utf8).pipeThrough(new CompressionStream('deflate-raw'));
      const compressed = await readStreamWithLimit(stream, Number.MAX_SAFE_INTEGER);
      return 'z.' + bytesToBase64Url(compressed);
    } catch {
      /* val terug op ongecomprimeerd */
    }
  }
  return 'u.' + bytesToBase64Url(utf8);
}

/** Gooit een ShareError (Nederlandse `message`) bij ongeldige/te grote payload. Retourneert ruwe (nog niet opgeschoonde) items. */
export async function decodeSharePayload(raw: string): Promise<unknown[]> {
  const str = String(raw ?? '').trim();
  if (!str) throw new ShareError(MSG_EMPTY);
  if (str.length > MAX_SHARE_PARAM_LENGTH) throw new ShareError(MSG_TOO_LONG);

  let json: string;
  if (str.startsWith('z.')) {
    if (typeof DecompressionStream !== 'function') throw new ShareError(MSG_NO_DECOMPRESS);
    const bytes = base64UrlToBytes(str.slice(2));
    let decompressed: Uint8Array;
    try {
      const stream = streamOf(bytes).pipeThrough(
        new DecompressionStream('deflate-raw'),
      );
      decompressed = await readStreamWithLimit(stream, MAX_SHARE_DECODED_BYTES);
    } catch (e) {
      if (e instanceof ShareError) throw e;
      throw new ShareError(MSG_BROKEN);
    }
    json = decodeUtf8(decompressed);
  } else if (str.startsWith('u.')) {
    const bytes = base64UrlToBytes(str.slice(2));
    if (bytes.length > MAX_SHARE_DECODED_BYTES) throw new ShareError(MSG_TOO_MUCH);
    json = decodeUtf8(bytes);
  } else {
    // Oud formaat. URLSearchParams maakt van '+' een spatie: herstel dat eerst.
    const legacy = str.replace(/ /g, '+');
    try {
      json = decodeURIComponent(atob(legacy));
    } catch {
      throw new ShareError(MSG_BROKEN);
    }
    if (json.length > MAX_SHARE_DECODED_BYTES) throw new ShareError(MSG_TOO_MUCH);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ShareError(MSG_BROKEN);
  }
  if (!Array.isArray(parsed)) throw new ShareError(MSG_NO_LIST);
  return parsed;
}

/** `${origin}${pathname}?share=<payload>`. De payload is al URL-veilig (base64url), maar wordt voor de zekerheid gecodeerd. */
export function buildShareUrl(origin: string, pathname: string, payload: string): string {
  return `${origin}${pathname}?share=${encodeURIComponent(payload)}`;
}
